import { defineMessages } from '@/i18n'

/**
 * The Projects list, project detail, project requests and the edit / new-request dialogs.
 *
 * AU's project status ladder is translated (it is a code → label map in the client, not data);
 * the abbreviations AU uses inside it — NDF, LLD, TB (Tiefbau) — stay as they are.
 */
export const projectsMessages = defineMessages({
  en: {
    // Status ladder (AU codes 0–12)
    status0: 'NDF',
    status1: 'Inquiry',
    status2: 'Planning',
    status3: 'In negotiation',
    status4: 'Contract signed',
    status5: 'Kick-off held',
    status6: 'LLD planning',
    status7: 'Permit submitted',
    status8: 'Permit received',
    status9: 'TB start',
    status10: 'TB end',
    status11: 'Accepted by the city',
    status12: 'Under construction – final phase',
    statusNone: 'No status',
    statusUnknown: 'Status {code}',

    // Calculation types (AU CalcType)
    calc0: 'Not defined',
    calc1: 'HP + metres',
    calc2: 'HP only',
    calc3: 'Metres only',
    calc4: 'NAS',

    // List page
    loadingProjects: 'Loading projects',
    subtitle: 'All live projects from AU — status, plan vs. executed, and schedule.',
    provider: 'Provider',
    rolloutManager: 'Rollout manager',
    status: 'Status',
    allProviders: 'All providers',
    allRoms: 'All ROMs',
    allStatuses: 'All statuses',
    searchPlaceholder: 'name or internal number…',
    searchProjects: 'Search projects',
    addProject: '+ Add project',
    tabActive: 'Active this month',
    tabMissing: 'Missing data',
    tabRequests: 'Requests',
    workedWithin: 'worked within 31 days of {date}',
    colProject: 'Project',
    crewDays: 'Crew-days',
    colProgress: 'Plan → executed',
    colSub: 'SUB €',
    colSchedule: 'TB schedule',
    lastWorked: 'Last worked',
    unnamed: '(unnamed)',
    noWorkYet: '· no work yet',
    notPlanned: 'not planned',
    noPlan: 'no plan · {metres}',
    progressTitle: '{done} of {planned} planned',
    missingPlannedM: 'Planned m',
    missingContract: 'Contract €',
    missingTbDates: 'TB dates',
    emptyActive: 'No projects worked in the last month.',
    emptyMissing: 'No projects are missing data — all complete. 🎉',
    emptyAll: 'No projects match.',
    filterProjects: 'Filter projects',
    projectsMatch: { one: '{count} project matches', other: '{count} projects match' },
    showProjects: { one: 'Show {count} project', other: 'Show {count} projects' },
    perPage: '{n} / page',
    rangeOf: '{from}–{to} of {total}',
    noneOf: '{n} of {total}',

    // Detail page
    loadingProject: 'Loading project',
    projectNotFound: 'Project not found.',
    unnamedProject: '(unnamed project)',
    codeInternalShort: 'Intern {code}',
    codeExternalShort: 'Extern {code}',
    executedMetres: 'Executed metres',
    subRevenue: 'SUB revenue',
    manHours: 'Man-hours',
    contract: 'Contract',
    contractValue: 'Contract value',
    projectValue: 'Project value',
    plannedMetres: 'Planned metres',
    plannedHp: 'Planned HP',
    unitPricing: 'Unit pricing',
    perMetre: 'Per metre',
    perHp: 'Per HP',
    perHas: 'Per HAS',
    calcType: 'Calc type',
    activity: 'Activity',
    firstWorked: 'First worked',
    workingDays: 'Working days',
    crewsInvolved: 'Crews involved',
    tbWindow: 'TB window',
    executedByWeek: 'Executed metres by week',
    subByWeek: 'SUB revenue by week',
    whatWasBuilt: 'What was built',
    quantityByPosition: 'Quantity by workbook position',
    crewsOnProject: 'Crews on this project',
    notRecorded: 'not recorded in AU',

    // Requests
    noRequests: 'No project requests yet. Use {action} to stage one for AU.',
    addProjectAction: 'Add project',
    byName: ' · by {name}',
    keyedIn: 'Keyed in',
    open: 'Open',
    plannedM: 'Planned m',
    contractEur: 'Contract €',
    priceList: 'Price list',
    copied: 'Copied ✓',
    copyDetails: 'Copy details',
    markKeyedIn: 'Mark keyed in',
    deleteConfirm: 'Delete?',
    projectName: 'Project name',
    workingName: 'Working name',
    codeInternal: 'Code internal',
    codeExternal: 'Code external',
    country: 'Country',
    projectManager: 'Project manager',
    planning: 'Planning',
    contact: 'Contact',
    contractNo: 'Contract #',
    description: 'Description',
    note: 'Note',

    // Edit dialog
    editProject: 'Edit project',
    couldNotSave: 'Could not save.',
    identity: 'Identity',
    assignment: 'Assignment',
    planningValues: 'Planning & values',
    projectShortName: 'Project short name',
    contactName: 'Contact name',
    contractNumber: 'Contract number',
    noneOption: '— none —',
    planningStart: 'Planning start',
    planningEnd: 'Planning end',
    tbStart: 'TB start',
    tbEnd: 'TB end',
    tbPlannedM: 'TB planned (m)',
    hpPlannedPcs: 'HP planned (pcs)',
    contractValueEur: 'Contract value (€)',
    projectValueEur: 'Project value (€)',
    editNote: 'Price lists and calculation type are coming in the next pass. Edits save to Aufmass and are layered over AU on read — AU is never changed.',
    saving: 'Saving…',

    // New request wizard
    newRequest: 'New project request',
    newRequestSubtitle: 'Staged for AU — someone keys it in, then marks it done. Nothing is written to AU.',
    projectNameRequired: 'Project name *',
    findProvider: 'Find a provider…',
    findPerson: 'Find a person…',
    pickedFromAu: 'Provider/ROM/PM are picked from AU’s lists so whoever keys this in can match them exactly.',
    priceListNote: 'Price list (note)',
    priceListPlaceholder: 'e.g. UGG Juli',
    noteForKeyer: 'Note for whoever keys this in',
    stepOf: 'Step {step} of {total}',
    back: 'Back',
    next: 'Next',
    submitting: 'Submitting…',
    submitRequest: 'Submit request',
    couldNotSubmit: 'Could not submit the request.',
    cableTitle: 'Cable — declared vs laid',
    cableSubtitle: 'Metres of each cable (duct) the project declares, against the metres its crews reported laying in their Aufmaß reports.',
    cableCouldNotLoad: 'Could not load the cable figures.',
    cableColCable: 'Cable',
    cableColDeclared: 'Declared',
    cableColLaid: 'Laid',
    cableColProgress: 'Laid of declared',
    cableColCrews: 'Crews',
    cableColLastLaid: 'Last laid',
    cableNotInCatalogue: 'Not in catalogue',
    cableNotInCatalogueHint: "Reported under a cable question that no longer exists in AU — identified by its name.",
    cableImplausibleHint: { one: '{count} entry kept out of the total (see below)', other: '{count} entries kept out of the total (see below)' },
    cableDeclare: 'Declare',
    cableEdit: 'Edit',
    cableDeclareNew: 'Declare cable',
    cableTileDeclared: 'Declared',
    cableTileLaidDeclared: 'Laid (declared cables)',
    cableTileRemaining: 'Still to lay',
    cableTileLaidAll: 'Laid (all cables)',
    cableEmpty: 'No cable declared or reported on this project yet.',
    cableByCrew: 'Laid by crew',
    cableImplausibleTitle: 'Kept out of the totals',
    cableImplausibleBody: 'Single entries over {max} m — usually a drum length typed as metres, or a placeholder. Correct them in AU; they count once they are plausible.',
    cableGroupLaid: 'Laid on this project',
    cableGroupOwn: "Project's provider",
    cableGroupOther: 'Other providers',
    cableDeclareTitle: 'Declare cable',
    cableEditTitle: 'Change declared cable',
    cableDialogHint: 'How many metres of this cable the project needs.',
    cableSearch: 'Search cable or provider…',
    cableDeclaredMetres: 'Declared metres',
    cableNote: 'Note (optional)',
    cableRemove: 'Remove from declaration',
    cableRemoveConfirm: 'Remove this cable from the declaration?',
    cableColOnSite: 'On site (drums)',
    cableColUnaccounted: 'Unaccounted',
    cableUnaccountedHint: 'Off the drums minus reported laid since the first drum arrived — see Inventory → Reconciliation.',
  },
  de: {
    status0: 'NDF',
    status1: 'Anfrage',
    status2: 'Planung',
    status3: 'In Verhandlung',
    status4: 'Vertrag gezeichnet',
    status5: 'Kick-Off durchgeführt',
    status6: 'LLD Planung',
    status7: 'Genehmigung eingereicht',
    status8: 'Genehmigung erhalten',
    status9: 'TB Start',
    status10: 'TB Ende',
    status11: 'Abnahme durch die Stadt',
    status12: 'in Bau – Endphase',
    statusNone: 'Kein Status',
    statusUnknown: 'Status {code}',

    calc0: 'Nicht definiert',
    calc1: 'HP + Meter',
    calc2: 'Nur HP',
    calc3: 'Nur Meter',
    calc4: 'NAS',

    loadingProjects: 'Projekte werden geladen',
    subtitle: 'Alle laufenden Projekte aus AU — Status, Plan vs. Ist und Zeitplan.',
    provider: 'Auftraggeber',
    rolloutManager: 'Rollout-Manager',
    status: 'Status',
    allProviders: 'Alle Auftraggeber',
    allRoms: 'Alle ROMs',
    allStatuses: 'Alle Status',
    searchPlaceholder: 'Name oder interne Nummer…',
    searchProjects: 'Projekte durchsuchen',
    addProject: '+ Projekt hinzufügen',
    tabActive: 'Aktiv diesen Monat',
    tabMissing: 'Fehlende Daten',
    tabRequests: 'Anfragen',
    workedWithin: 'gearbeitet innerhalb von 31 Tagen vor dem {date}',
    colProject: 'Projekt',
    crewDays: 'Kolonnentage',
    colProgress: 'Plan → Ist',
    colSub: 'SUB €',
    colSchedule: 'TB-Zeitplan',
    lastWorked: 'Zuletzt gearbeitet',
    unnamed: '(ohne Namen)',
    noWorkYet: '· noch keine Arbeiten',
    notPlanned: 'nicht geplant',
    noPlan: 'kein Plan · {metres}',
    progressTitle: '{done} von {planned} geplant',
    missingPlannedM: 'Geplante m',
    missingContract: 'Vertrag €',
    missingTbDates: 'TB-Termine',
    emptyActive: 'Keine Projekte im letzten Monat bearbeitet.',
    emptyMissing: 'Bei keinem Projekt fehlen Daten — alles vollständig. 🎉',
    emptyAll: 'Keine passenden Projekte.',
    filterProjects: 'Projekte filtern',
    projectsMatch: { one: '{count} Projekt passt', other: '{count} Projekte passen' },
    showProjects: { one: '{count} Projekt anzeigen', other: '{count} Projekte anzeigen' },
    perPage: '{n} / Seite',
    rangeOf: '{from}–{to} von {total}',
    noneOf: '{n} von {total}',

    loadingProject: 'Projekt wird geladen',
    projectNotFound: 'Projekt nicht gefunden.',
    unnamedProject: '(Projekt ohne Namen)',
    codeInternalShort: 'Intern {code}',
    codeExternalShort: 'Extern {code}',
    executedMetres: 'Ausgeführte Meter',
    subRevenue: 'SUB-Umsatz',
    manHours: 'Mannstunden',
    contract: 'Vertrag',
    contractValue: 'Vertragswert',
    projectValue: 'Projektwert',
    plannedMetres: 'Geplante Meter',
    plannedHp: 'Geplante HP',
    unitPricing: 'Einheitspreise',
    perMetre: 'Pro Meter',
    perHp: 'Pro HP',
    perHas: 'Pro HAS',
    calcType: 'Abrechnungsart',
    activity: 'Aktivität',
    firstWorked: 'Erster Arbeitstag',
    workingDays: 'Arbeitstage',
    crewsInvolved: 'Beteiligte Kolonnen',
    tbWindow: 'TB-Zeitraum',
    executedByWeek: 'Ausgeführte Meter pro Woche',
    subByWeek: 'SUB-Umsatz pro Woche',
    whatWasBuilt: 'Was gebaut wurde',
    quantityByPosition: 'Menge nach Aufmaß-Position',
    crewsOnProject: 'Kolonnen auf diesem Projekt',
    notRecorded: 'in AU nicht erfasst',

    noRequests: 'Noch keine Projektanfragen. Mit {action} legen Sie eine für AU an.',
    addProjectAction: 'Projekt hinzufügen',
    byName: ' · von {name}',
    keyedIn: 'Eingetragen',
    open: 'Offen',
    plannedM: 'Geplante m',
    contractEur: 'Vertrag €',
    priceList: 'Preisliste',
    copied: 'Kopiert ✓',
    copyDetails: 'Details kopieren',
    markKeyedIn: 'Als eingetragen markieren',
    deleteConfirm: 'Löschen?',
    projectName: 'Projektname',
    workingName: 'Arbeitsname',
    codeInternal: 'Code intern',
    codeExternal: 'Code extern',
    country: 'Land',
    projectManager: 'Projektleiter',
    planning: 'Planung',
    contact: 'Ansprechpartner',
    contractNo: 'Vertrags-Nr.',
    description: 'Beschreibung',
    note: 'Notiz',

    editProject: 'Projekt bearbeiten',
    couldNotSave: 'Speichern fehlgeschlagen.',
    identity: 'Stammdaten',
    assignment: 'Zuständigkeit',
    planningValues: 'Planung & Werte',
    projectShortName: 'Projekt-Kurzname',
    contactName: 'Ansprechpartner',
    contractNumber: 'Vertragsnummer',
    noneOption: '— keine —',
    planningStart: 'Planungsbeginn',
    planningEnd: 'Planungsende',
    tbStart: 'TB Start',
    tbEnd: 'TB Ende',
    tbPlannedM: 'TB geplant (m)',
    hpPlannedPcs: 'HP geplant (Stk.)',
    contractValueEur: 'Vertragswert (€)',
    projectValueEur: 'Projektwert (€)',
    editNote: 'Preislisten und Abrechnungsart folgen im nächsten Schritt. Änderungen werden in Aufmass gespeichert und beim Lesen über AU gelegt — AU selbst wird nie geändert.',
    saving: 'Wird gespeichert…',

    newRequest: 'Neue Projektanfrage',
    newRequestSubtitle: 'Für AU vorgemerkt — jemand trägt sie ein und markiert sie dann als erledigt. In AU wird nichts geschrieben.',
    projectNameRequired: 'Projektname *',
    findProvider: 'Auftraggeber suchen…',
    findPerson: 'Person suchen…',
    pickedFromAu: 'Auftraggeber/ROM/PL werden aus den Listen von AU gewählt, damit sie beim Eintragen exakt zugeordnet werden können.',
    priceListNote: 'Preisliste (Notiz)',
    priceListPlaceholder: 'z. B. UGG Juli',
    noteForKeyer: 'Notiz für die Person, die es einträgt',
    stepOf: 'Schritt {step} von {total}',
    back: 'Zurück',
    next: 'Weiter',
    submitting: 'Wird gesendet…',
    submitRequest: 'Anfrage senden',
    couldNotSubmit: 'Die Anfrage konnte nicht gesendet werden.',
    cableTitle: 'Kabel — vorgesehen vs. verlegt',
    cableSubtitle: 'Meter je Kabel (Rohrverband), die das Projekt vorsieht, gegenüber den Metern, die die Kolonnen in ihren Aufmaß-Berichten als verlegt gemeldet haben.',
    cableCouldNotLoad: 'Die Kabelzahlen konnten nicht geladen werden.',
    cableColCable: 'Kabel',
    cableColDeclared: 'Vorgesehen',
    cableColLaid: 'Verlegt',
    cableColProgress: 'Verlegt vom Vorgesehenen',
    cableColCrews: 'Kolonnen',
    cableColLastLaid: 'Zuletzt verlegt',
    cableNotInCatalogue: 'Nicht im Katalog',
    cableNotInCatalogueHint: 'Unter einer Kabelfrage gemeldet, die es in AU nicht mehr gibt — über den Namen zugeordnet.',
    cableImplausibleHint: { one: '{count} Eintrag nicht in der Summe (siehe unten)', other: '{count} Einträge nicht in der Summe (siehe unten)' },
    cableDeclare: 'Vorsehen',
    cableEdit: 'Bearbeiten',
    cableDeclareNew: 'Kabel vorsehen',
    cableTileDeclared: 'Vorgesehen',
    cableTileLaidDeclared: 'Verlegt (vorgesehene Kabel)',
    cableTileRemaining: 'Noch zu verlegen',
    cableTileLaidAll: 'Verlegt (alle Kabel)',
    cableEmpty: 'Für dieses Projekt ist noch kein Kabel vorgesehen oder gemeldet.',
    cableByCrew: 'Verlegt nach Kolonne',
    cableImplausibleTitle: 'Nicht in den Summen',
    cableImplausibleBody: 'Einzelne Einträge über {max} m — meist eine als Meter eingetragene Trommellänge oder ein Platzhalter. In AU korrigieren; plausibel zählen sie mit.',
    cableGroupLaid: 'Auf diesem Projekt verlegt',
    cableGroupOwn: 'Netzbetreiber des Projekts',
    cableGroupOther: 'Andere Netzbetreiber',
    cableDeclareTitle: 'Kabel vorsehen',
    cableEditTitle: 'Vorgesehenes Kabel ändern',
    cableDialogHint: 'Wie viele Meter dieses Kabels das Projekt braucht.',
    cableSearch: 'Kabel oder Netzbetreiber suchen…',
    cableDeclaredMetres: 'Vorgesehene Meter',
    cableNote: 'Notiz (optional)',
    cableRemove: 'Aus der Planung entfernen',
    cableRemoveConfirm: 'Dieses Kabel aus der Planung entfernen?',
    cableColOnSite: 'Vor Ort (Trommeln)',
    cableColUnaccounted: 'Ungeklärt',
    cableUnaccountedHint: 'Von den Trommeln minus als verlegt gemeldet seit Eingang der ersten Trommel — siehe Lager → Abgleich.',
  },
  cnr: {
    status0: 'NDF',
    status1: 'Upit',
    status2: 'Planiranje',
    status3: 'U pregovorima',
    status4: 'Ugovor potpisan',
    status5: 'Kick-off održan',
    status6: 'LLD planiranje',
    status7: 'Zahtjev za dozvolu podnesen',
    status8: 'Dozvola dobijena',
    status9: 'TB početak',
    status10: 'TB kraj',
    status11: 'Prijem od strane grada',
    status12: 'U izgradnji – završna faza',
    statusNone: 'Bez statusa',
    statusUnknown: 'Status {code}',

    calc0: 'Nije definisano',
    calc1: 'HP + metri',
    calc2: 'Samo HP',
    calc3: 'Samo metri',
    calc4: 'NAS',

    loadingProjects: 'Učitavanje projekata',
    subtitle: 'Svi aktivni projekti iz AU — status, plan naspram izvršenog i raspored.',
    provider: 'Naručilac',
    rolloutManager: 'Rollout menadžer',
    status: 'Status',
    allProviders: 'Svi naručioci',
    allRoms: 'Svi ROM-ovi',
    allStatuses: 'Svi statusi',
    searchPlaceholder: 'naziv ili interni broj…',
    searchProjects: 'Pretraži projekte',
    addProject: '+ Dodaj projekat',
    tabActive: 'Aktivni ovog mjeseca',
    tabMissing: 'Nedostaju podaci',
    tabRequests: 'Zahtjevi',
    workedWithin: 'rađeno u 31 dan do {date}',
    colProject: 'Projekat',
    crewDays: 'Dani kolona',
    colProgress: 'Plan → izvršeno',
    colSub: 'SUB €',
    colSchedule: 'TB raspored',
    lastWorked: 'Posljednji rad',
    unnamed: '(bez naziva)',
    noWorkYet: '· još bez radova',
    notPlanned: 'nije planirano',
    noPlan: 'bez plana · {metres}',
    progressTitle: '{done} od {planned} planiranih',
    missingPlannedM: 'Planirani m',
    missingContract: 'Ugovor €',
    missingTbDates: 'TB datumi',
    emptyActive: 'Nijedan projekat nije rađen u posljednjih mjesec dana.',
    emptyMissing: 'Nijednom projektu ne nedostaju podaci — sve je kompletno. 🎉',
    emptyAll: 'Nema odgovarajućih projekata.',
    filterProjects: 'Filtriraj projekte',
    projectsMatch: { one: '{count} projekat odgovara', few: '{count} projekta odgovaraju', other: '{count} projekata odgovara' },
    showProjects: { one: 'Prikaži {count} projekat', few: 'Prikaži {count} projekta', other: 'Prikaži {count} projekata' },
    perPage: '{n} / strana',
    rangeOf: '{from}–{to} od {total}',
    noneOf: '{n} od {total}',

    loadingProject: 'Učitavanje projekta',
    projectNotFound: 'Projekat nije pronađen.',
    unnamedProject: '(projekat bez naziva)',
    codeInternalShort: 'Interno {code}',
    codeExternalShort: 'Eksterno {code}',
    executedMetres: 'Izvršeni metri',
    subRevenue: 'SUB prihod',
    manHours: 'Radni sati',
    contract: 'Ugovor',
    contractValue: 'Vrijednost ugovora',
    projectValue: 'Vrijednost projekta',
    plannedMetres: 'Planirani metri',
    plannedHp: 'Planirani HP',
    unitPricing: 'Jedinične cijene',
    perMetre: 'Po metru',
    perHp: 'Po HP',
    perHas: 'Po HAS',
    calcType: 'Vrsta obračuna',
    activity: 'Aktivnost',
    firstWorked: 'Prvi radni dan',
    workingDays: 'Radni dani',
    crewsInvolved: 'Uključene kolone',
    tbWindow: 'TB period',
    executedByWeek: 'Izvršeni metri po nedjeljama',
    subByWeek: 'SUB prihod po nedjeljama',
    whatWasBuilt: 'Šta je izgrađeno',
    quantityByPosition: 'Količina po poziciji Aufmaß-a',
    crewsOnProject: 'Kolone na ovom projektu',
    notRecorded: 'nije evidentirano u AU',

    noRequests: 'Još nema zahtjeva za projekte. Koristite {action} da pripremite jedan za AU.',
    addProjectAction: 'Dodaj projekat',
    byName: ' · {name}',
    keyedIn: 'Unesen',
    open: 'Otvoren',
    plannedM: 'Planirani m',
    contractEur: 'Ugovor €',
    priceList: 'Cjenovnik',
    copied: 'Kopirano ✓',
    copyDetails: 'Kopiraj detalje',
    markKeyedIn: 'Označi kao unesen',
    deleteConfirm: 'Obrisati?',
    projectName: 'Naziv projekta',
    workingName: 'Radni naziv',
    codeInternal: 'Interna šifra',
    codeExternal: 'Eksterna šifra',
    country: 'Država',
    projectManager: 'Menadžer projekta',
    planning: 'Planiranje',
    contact: 'Kontakt',
    contractNo: 'Br. ugovora',
    description: 'Opis',
    note: 'Napomena',

    editProject: 'Izmijeni projekat',
    couldNotSave: 'Čuvanje nije uspjelo.',
    identity: 'Osnovni podaci',
    assignment: 'Zaduženja',
    planningValues: 'Planiranje i vrijednosti',
    projectShortName: 'Kratki naziv projekta',
    contactName: 'Kontakt osoba',
    contractNumber: 'Broj ugovora',
    noneOption: '— nijedan —',
    planningStart: 'Početak planiranja',
    planningEnd: 'Kraj planiranja',
    tbStart: 'TB početak',
    tbEnd: 'TB kraj',
    tbPlannedM: 'TB planirano (m)',
    hpPlannedPcs: 'HP planirano (kom)',
    contractValueEur: 'Vrijednost ugovora (€)',
    projectValueEur: 'Vrijednost projekta (€)',
    editNote: 'Cjenovnici i vrsta obračuna stižu u sljedećoj fazi. Izmjene se čuvaju u Aufmassu i pri čitanju se primjenjuju preko AU — AU se nikad ne mijenja.',
    saving: 'Čuvanje…',

    newRequest: 'Novi zahtjev za projekat',
    newRequestSubtitle: 'Pripremljeno za AU — neko ga unese, pa označi kao završen. U AU se ništa ne upisuje.',
    projectNameRequired: 'Naziv projekta *',
    findProvider: 'Pronađi naručioca…',
    findPerson: 'Pronađi osobu…',
    pickedFromAu: 'Naručilac/ROM/PM se biraju iz lista u AU, kako bi ih onaj ko unosi mogao tačno upariti.',
    priceListNote: 'Cjenovnik (napomena)',
    priceListPlaceholder: 'npr. UGG Juli',
    noteForKeyer: 'Napomena za onoga ko unosi',
    stepOf: 'Korak {step} od {total}',
    back: 'Nazad',
    next: 'Dalje',
    submitting: 'Slanje…',
    submitRequest: 'Pošalji zahtjev',
    couldNotSubmit: 'Slanje zahtjeva nije uspjelo.',
    cableTitle: 'Kabl — planirano i položeno',
    cableSubtitle: 'Metri svakog kabla (cijevi) koje projekat planira, u odnosu na metre koje su kolone prijavile kao položene u Aufmaß izvještajima.',
    cableCouldNotLoad: 'Nije moguće učitati podatke o kablu.',
    cableColCable: 'Kabl',
    cableColDeclared: 'Planirano',
    cableColLaid: 'Položeno',
    cableColProgress: 'Položeno od planiranog',
    cableColCrews: 'Kolone',
    cableColLastLaid: 'Posljednje polaganje',
    cableNotInCatalogue: 'Nije u katalogu',
    cableNotInCatalogueHint: 'Prijavljeno pod pitanjem za kabl koje više ne postoji u AU — prepoznato po nazivu.',
    cableImplausibleHint: { one: '{count} unos izostavljen iz zbira (vidi ispod)', few: '{count} unosa izostavljena iz zbira (vidi ispod)', other: '{count} unosa izostavljeno iz zbira (vidi ispod)' },
    cableDeclare: 'Planiraj',
    cableEdit: 'Izmijeni',
    cableDeclareNew: 'Planiraj kabl',
    cableTileDeclared: 'Planirano',
    cableTileLaidDeclared: 'Položeno (planirani kablovi)',
    cableTileRemaining: 'Preostalo za polaganje',
    cableTileLaidAll: 'Položeno (svi kablovi)',
    cableEmpty: 'Za ovaj projekat još nije planiran niti prijavljen kabl.',
    cableByCrew: 'Položeno po kolonama',
    cableImplausibleTitle: 'Izostavljeno iz zbirova',
    cableImplausibleBody: 'Pojedinačni unosi preko {max} m — obično dužina bubnja upisana kao metri, ili privremena vrijednost. Ispravite ih u AU; računaju se kad postanu realni.',
    cableGroupLaid: 'Položeno na ovom projektu',
    cableGroupOwn: 'Provajder projekta',
    cableGroupOther: 'Drugi provajderi',
    cableDeclareTitle: 'Planiraj kabl',
    cableEditTitle: 'Izmijeni planirani kabl',
    cableDialogHint: 'Koliko metara ovog kabla projektu treba.',
    cableSearch: 'Traži kabl ili provajdera…',
    cableDeclaredMetres: 'Planirani metri',
    cableNote: 'Napomena (neobavezno)',
    cableRemove: 'Ukloni iz plana',
    cableRemoveConfirm: 'Ukloniti ovaj kabl iz plana?',
    cableColOnSite: 'Na gradilištu (bubnjevi)',
    cableColUnaccounted: 'Neobjašnjeno',
    cableUnaccountedHint: 'Skinuto sa bubnjeva minus prijavljeno položeno od dolaska prvog bubnja — vidi Magacin → Usklađivanje.',
  },
  bs: {
    status0: 'NDF',
    status1: 'Upit',
    status2: 'Planiranje',
    status3: 'U pregovorima',
    status4: 'Ugovor potpisan',
    status5: 'Kick-off održan',
    status6: 'LLD planiranje',
    status7: 'Zahtjev za dozvolu podnesen',
    status8: 'Dozvola dobijena',
    status9: 'TB početak',
    status10: 'TB kraj',
    status11: 'Prijem od strane grada',
    status12: 'U izgradnji – završna faza',
    statusNone: 'Bez statusa',
    statusUnknown: 'Status {code}',

    calc0: 'Nije definisano',
    calc1: 'HP + metri',
    calc2: 'Samo HP',
    calc3: 'Samo metri',
    calc4: 'NAS',

    loadingProjects: 'Učitavanje projekata',
    subtitle: 'Svi aktivni projekti iz AU — status, plan naspram izvršenog i raspored.',
    provider: 'Naručilac',
    rolloutManager: 'Rollout menadžer',
    status: 'Status',
    allProviders: 'Svi naručioci',
    allRoms: 'Svi ROM-ovi',
    allStatuses: 'Svi statusi',
    searchPlaceholder: 'naziv ili interni broj…',
    searchProjects: 'Pretraži projekte',
    addProject: '+ Dodaj projekat',
    tabActive: 'Aktivni ovog mjeseca',
    tabMissing: 'Nedostaju podaci',
    tabRequests: 'Zahtjevi',
    workedWithin: 'rađeno u 31 dan do {date}',
    colProject: 'Projekat',
    crewDays: 'Dani kolona',
    colProgress: 'Plan → izvršeno',
    colSub: 'SUB €',
    colSchedule: 'TB raspored',
    lastWorked: 'Posljednji rad',
    unnamed: '(bez naziva)',
    noWorkYet: '· još bez radova',
    notPlanned: 'nije planirano',
    noPlan: 'bez plana · {metres}',
    progressTitle: '{done} od {planned} planiranih',
    missingPlannedM: 'Planirani m',
    missingContract: 'Ugovor €',
    missingTbDates: 'TB datumi',
    emptyActive: 'Nijedan projekat nije rađen u posljednjih mjesec dana.',
    emptyMissing: 'Nijednom projektu ne nedostaju podaci — sve je kompletno. 🎉',
    emptyAll: 'Nema odgovarajućih projekata.',
    filterProjects: 'Filtriraj projekte',
    projectsMatch: { one: '{count} projekat odgovara', few: '{count} projekta odgovaraju', other: '{count} projekata odgovara' },
    showProjects: { one: 'Prikaži {count} projekat', few: 'Prikaži {count} projekta', other: 'Prikaži {count} projekata' },
    perPage: '{n} / stranica',
    rangeOf: '{from}–{to} od {total}',
    noneOf: '{n} od {total}',

    loadingProject: 'Učitavanje projekta',
    projectNotFound: 'Projekat nije pronađen.',
    unnamedProject: '(projekat bez naziva)',
    codeInternalShort: 'Interno {code}',
    codeExternalShort: 'Eksterno {code}',
    executedMetres: 'Izvršeni metri',
    subRevenue: 'SUB prihod',
    manHours: 'Radni sati',
    contract: 'Ugovor',
    contractValue: 'Vrijednost ugovora',
    projectValue: 'Vrijednost projekta',
    plannedMetres: 'Planirani metri',
    plannedHp: 'Planirani HP',
    unitPricing: 'Jedinične cijene',
    perMetre: 'Po metru',
    perHp: 'Po HP',
    perHas: 'Po HAS',
    calcType: 'Vrsta obračuna',
    activity: 'Aktivnost',
    firstWorked: 'Prvi radni dan',
    workingDays: 'Radni dani',
    crewsInvolved: 'Uključene kolone',
    tbWindow: 'TB period',
    executedByWeek: 'Izvršeni metri po sedmicama',
    subByWeek: 'SUB prihod po sedmicama',
    whatWasBuilt: 'Šta je izgrađeno',
    quantityByPosition: 'Količina po poziciji Aufmaß-a',
    crewsOnProject: 'Kolone na ovom projektu',
    notRecorded: 'nije evidentirano u AU',

    noRequests: 'Još nema zahtjeva za projekte. Koristite {action} da pripremite jedan za AU.',
    addProjectAction: 'Dodaj projekat',
    byName: ' · {name}',
    keyedIn: 'Unesen',
    open: 'Otvoren',
    plannedM: 'Planirani m',
    contractEur: 'Ugovor €',
    priceList: 'Cjenovnik',
    copied: 'Kopirano ✓',
    copyDetails: 'Kopiraj detalje',
    markKeyedIn: 'Označi kao unesen',
    deleteConfirm: 'Izbrisati?',
    projectName: 'Naziv projekta',
    workingName: 'Radni naziv',
    codeInternal: 'Interna šifra',
    codeExternal: 'Eksterna šifra',
    country: 'Država',
    projectManager: 'Menadžer projekta',
    planning: 'Planiranje',
    contact: 'Kontakt',
    contractNo: 'Br. ugovora',
    description: 'Opis',
    note: 'Napomena',

    editProject: 'Uredi projekat',
    couldNotSave: 'Spašavanje nije uspjelo.',
    identity: 'Osnovni podaci',
    assignment: 'Zaduženja',
    planningValues: 'Planiranje i vrijednosti',
    projectShortName: 'Kratki naziv projekta',
    contactName: 'Kontakt osoba',
    contractNumber: 'Broj ugovora',
    noneOption: '— nijedan —',
    planningStart: 'Početak planiranja',
    planningEnd: 'Kraj planiranja',
    tbStart: 'TB početak',
    tbEnd: 'TB kraj',
    tbPlannedM: 'TB planirano (m)',
    hpPlannedPcs: 'HP planirano (kom)',
    contractValueEur: 'Vrijednost ugovora (€)',
    projectValueEur: 'Vrijednost projekta (€)',
    editNote: 'Cjenovnici i vrsta obračuna stižu u sljedećoj fazi. Izmjene se spašavaju u Aufmassu i pri čitanju se primjenjuju preko AU — AU se nikad ne mijenja.',
    saving: 'Spašavanje…',

    newRequest: 'Novi zahtjev za projekat',
    newRequestSubtitle: 'Pripremljeno za AU — neko ga unese, pa označi kao završen. U AU se ništa ne upisuje.',
    projectNameRequired: 'Naziv projekta *',
    findProvider: 'Pronađi naručioca…',
    findPerson: 'Pronađi osobu…',
    pickedFromAu: 'Naručilac/ROM/PM se biraju iz lista u AU, kako bi ih onaj ko unosi mogao tačno upariti.',
    priceListNote: 'Cjenovnik (napomena)',
    priceListPlaceholder: 'npr. UGG Juli',
    noteForKeyer: 'Napomena za onoga ko unosi',
    stepOf: 'Korak {step} od {total}',
    back: 'Nazad',
    next: 'Dalje',
    submitting: 'Slanje…',
    submitRequest: 'Pošalji zahtjev',
    couldNotSubmit: 'Slanje zahtjeva nije uspjelo.',
    cableTitle: 'Kabl — planirano i položeno',
    cableSubtitle: 'Metri svakog kabla (cijevi) koje projekat planira, u odnosu na metre koje su kolone prijavile kao položene u Aufmaß izvještajima.',
    cableCouldNotLoad: 'Nije moguće učitati podatke o kablu.',
    cableColCable: 'Kabl',
    cableColDeclared: 'Planirano',
    cableColLaid: 'Položeno',
    cableColProgress: 'Položeno od planiranog',
    cableColCrews: 'Kolone',
    cableColLastLaid: 'Posljednje polaganje',
    cableNotInCatalogue: 'Nije u katalogu',
    cableNotInCatalogueHint: 'Prijavljeno pod pitanjem za kabl koje više ne postoji u AU — prepoznato po nazivu.',
    cableImplausibleHint: { one: '{count} unos izostavljen iz zbira (vidi ispod)', few: '{count} unosa izostavljena iz zbira (vidi ispod)', other: '{count} unosa izostavljeno iz zbira (vidi ispod)' },
    cableDeclare: 'Planiraj',
    cableEdit: 'Uredi',
    cableDeclareNew: 'Planiraj kabl',
    cableTileDeclared: 'Planirano',
    cableTileLaidDeclared: 'Položeno (planirani kablovi)',
    cableTileRemaining: 'Preostalo za polaganje',
    cableTileLaidAll: 'Položeno (svi kablovi)',
    cableEmpty: 'Za ovaj projekat još nije planiran niti prijavljen kabl.',
    cableByCrew: 'Položeno po kolonama',
    cableImplausibleTitle: 'Izostavljeno iz zbirova',
    cableImplausibleBody: 'Pojedinačni unosi preko {max} m — obično dužina bubnja upisana kao metri, ili privremena vrijednost. Ispravite ih u AU; računaju se kad postanu realni.',
    cableGroupLaid: 'Položeno na ovom projektu',
    cableGroupOwn: 'Provajder projekta',
    cableGroupOther: 'Drugi provajderi',
    cableDeclareTitle: 'Planiraj kabl',
    cableEditTitle: 'Uredi planirani kabl',
    cableDialogHint: 'Koliko metara ovog kabla projektu treba.',
    cableSearch: 'Traži kabl ili provajdera…',
    cableDeclaredMetres: 'Planirani metri',
    cableNote: 'Napomena (neobavezno)',
    cableRemove: 'Ukloni iz plana',
    cableRemoveConfirm: 'Ukloniti ovaj kabl iz plana?',
    cableColOnSite: 'Na gradilištu (bubnjevi)',
    cableColUnaccounted: 'Neobjašnjeno',
    cableUnaccountedHint: 'Skinuto sa bubnjeva minus prijavljeno položeno od dolaska prvog bubnja — vidi Magacin → Usklađivanje.',
  },
  sr: {
    status0: 'NDF',
    status1: 'Upit',
    status2: 'Planiranje',
    status3: 'U pregovorima',
    status4: 'Ugovor potpisan',
    status5: 'Kick-off održan',
    status6: 'LLD planiranje',
    status7: 'Zahtev za dozvolu podnesen',
    status8: 'Dozvola dobijena',
    status9: 'TB početak',
    status10: 'TB kraj',
    status11: 'Prijem od strane grada',
    status12: 'U izgradnji – završna faza',
    statusNone: 'Bez statusa',
    statusUnknown: 'Status {code}',

    calc0: 'Nije definisano',
    calc1: 'HP + metri',
    calc2: 'Samo HP',
    calc3: 'Samo metri',
    calc4: 'NAS',

    loadingProjects: 'Učitavanje projekata',
    subtitle: 'Svi aktivni projekti iz AU — status, plan naspram izvršenog i raspored.',
    provider: 'Naručilac',
    rolloutManager: 'Rollout menadžer',
    status: 'Status',
    allProviders: 'Svi naručioci',
    allRoms: 'Svi ROM-ovi',
    allStatuses: 'Svi statusi',
    searchPlaceholder: 'naziv ili interni broj…',
    searchProjects: 'Pretraži projekte',
    addProject: '+ Dodaj projekat',
    tabActive: 'Aktivni ovog meseca',
    tabMissing: 'Nedostaju podaci',
    tabRequests: 'Zahtevi',
    workedWithin: 'rađeno u 31 dan do {date}',
    colProject: 'Projekat',
    crewDays: 'Dani kolona',
    colProgress: 'Plan → izvršeno',
    colSub: 'SUB €',
    colSchedule: 'TB raspored',
    lastWorked: 'Poslednji rad',
    unnamed: '(bez naziva)',
    noWorkYet: '· još bez radova',
    notPlanned: 'nije planirano',
    noPlan: 'bez plana · {metres}',
    progressTitle: '{done} od {planned} planiranih',
    missingPlannedM: 'Planirani m',
    missingContract: 'Ugovor €',
    missingTbDates: 'TB datumi',
    emptyActive: 'Nijedan projekat nije rađen u poslednjih mesec dana.',
    emptyMissing: 'Nijednom projektu ne nedostaju podaci — sve je kompletno. 🎉',
    emptyAll: 'Nema odgovarajućih projekata.',
    filterProjects: 'Filtriraj projekte',
    projectsMatch: { one: '{count} projekat odgovara', few: '{count} projekta odgovaraju', other: '{count} projekata odgovara' },
    showProjects: { one: 'Prikaži {count} projekat', few: 'Prikaži {count} projekta', other: 'Prikaži {count} projekata' },
    perPage: '{n} / strana',
    rangeOf: '{from}–{to} od {total}',
    noneOf: '{n} od {total}',

    loadingProject: 'Učitavanje projekta',
    projectNotFound: 'Projekat nije pronađen.',
    unnamedProject: '(projekat bez naziva)',
    codeInternalShort: 'Interno {code}',
    codeExternalShort: 'Eksterno {code}',
    executedMetres: 'Izvršeni metri',
    subRevenue: 'SUB prihod',
    manHours: 'Radni sati',
    contract: 'Ugovor',
    contractValue: 'Vrednost ugovora',
    projectValue: 'Vrednost projekta',
    plannedMetres: 'Planirani metri',
    plannedHp: 'Planirani HP',
    unitPricing: 'Jedinične cene',
    perMetre: 'Po metru',
    perHp: 'Po HP',
    perHas: 'Po HAS',
    calcType: 'Vrsta obračuna',
    activity: 'Aktivnost',
    firstWorked: 'Prvi radni dan',
    workingDays: 'Radni dani',
    crewsInvolved: 'Uključene kolone',
    tbWindow: 'TB period',
    executedByWeek: 'Izvršeni metri po nedeljama',
    subByWeek: 'SUB prihod po nedeljama',
    whatWasBuilt: 'Šta je izgrađeno',
    quantityByPosition: 'Količina po poziciji Aufmaß-a',
    crewsOnProject: 'Kolone na ovom projektu',
    notRecorded: 'nije evidentirano u AU',

    noRequests: 'Još nema zahteva za projekte. Koristite {action} da pripremite jedan za AU.',
    addProjectAction: 'Dodaj projekat',
    byName: ' · {name}',
    keyedIn: 'Unesen',
    open: 'Otvoren',
    plannedM: 'Planirani m',
    contractEur: 'Ugovor €',
    priceList: 'Cenovnik',
    copied: 'Kopirano ✓',
    copyDetails: 'Kopiraj detalje',
    markKeyedIn: 'Označi kao unesen',
    deleteConfirm: 'Obrisati?',
    projectName: 'Naziv projekta',
    workingName: 'Radni naziv',
    codeInternal: 'Interna šifra',
    codeExternal: 'Eksterna šifra',
    country: 'Država',
    projectManager: 'Menadžer projekta',
    planning: 'Planiranje',
    contact: 'Kontakt',
    contractNo: 'Br. ugovora',
    description: 'Opis',
    note: 'Napomena',

    editProject: 'Izmeni projekat',
    couldNotSave: 'Čuvanje nije uspelo.',
    identity: 'Osnovni podaci',
    assignment: 'Zaduženja',
    planningValues: 'Planiranje i vrednosti',
    projectShortName: 'Kratki naziv projekta',
    contactName: 'Kontakt osoba',
    contractNumber: 'Broj ugovora',
    noneOption: '— nijedan —',
    planningStart: 'Početak planiranja',
    planningEnd: 'Kraj planiranja',
    tbStart: 'TB početak',
    tbEnd: 'TB kraj',
    tbPlannedM: 'TB planirano (m)',
    hpPlannedPcs: 'HP planirano (kom)',
    contractValueEur: 'Vrednost ugovora (€)',
    projectValueEur: 'Vrednost projekta (€)',
    editNote: 'Cenovnici i vrsta obračuna stižu u sledećoj fazi. Izmene se čuvaju u Aufmassu i pri čitanju se primenjuju preko AU — AU se nikad ne menja.',
    saving: 'Čuvanje…',

    newRequest: 'Novi zahtev za projekat',
    newRequestSubtitle: 'Pripremljeno za AU — neko ga unese, pa označi kao završen. U AU se ništa ne upisuje.',
    projectNameRequired: 'Naziv projekta *',
    findProvider: 'Pronađi naručioca…',
    findPerson: 'Pronađi osobu…',
    pickedFromAu: 'Naručilac/ROM/PM se biraju iz lista u AU, kako bi ih onaj ko unosi mogao tačno upariti.',
    priceListNote: 'Cenovnik (napomena)',
    priceListPlaceholder: 'npr. UGG Juli',
    noteForKeyer: 'Napomena za onoga ko unosi',
    stepOf: 'Korak {step} od {total}',
    back: 'Nazad',
    next: 'Dalje',
    submitting: 'Slanje…',
    submitRequest: 'Pošalji zahtev',
    couldNotSubmit: 'Slanje zahteva nije uspelo.',
    cableTitle: 'Kabl — planirano i položeno',
    cableSubtitle: 'Metri svakog kabla (cevi) koje projekat planira, u odnosu na metre koje su kolone prijavile kao položene u Aufmaß izvještajima.',
    cableCouldNotLoad: 'Nije moguće učitati podatke o kablu.',
    cableColCable: 'Kabl',
    cableColDeclared: 'Planirano',
    cableColLaid: 'Položeno',
    cableColProgress: 'Položeno od planiranog',
    cableColCrews: 'Kolone',
    cableColLastLaid: 'Poslednje polaganje',
    cableNotInCatalogue: 'Nije u katalogu',
    cableNotInCatalogueHint: 'Prijavljeno pod pitanjem za kabl koje više ne postoji u AU — prepoznato po nazivu.',
    cableImplausibleHint: { one: '{count} unos izostavljen iz zbira (vidi ispod)', few: '{count} unosa izostavljena iz zbira (vidi ispod)', other: '{count} unosa izostavljeno iz zbira (vidi ispod)' },
    cableDeclare: 'Planiraj',
    cableEdit: 'Izmeni',
    cableDeclareNew: 'Planiraj kabl',
    cableTileDeclared: 'Planirano',
    cableTileLaidDeclared: 'Položeno (planirani kablovi)',
    cableTileRemaining: 'Preostalo za polaganje',
    cableTileLaidAll: 'Položeno (svi kablovi)',
    cableEmpty: 'Za ovaj projekat još nije planiran niti prijavljen kabl.',
    cableByCrew: 'Položeno po kolonama',
    cableImplausibleTitle: 'Izostavljeno iz zbirova',
    cableImplausibleBody: 'Pojedinačni unosi preko {max} m — obično dužina bubnja upisana kao metri, ili privremena vrednost. Ispravite ih u AU; računaju se kad postanu realni.',
    cableGroupLaid: 'Položeno na ovom projektu',
    cableGroupOwn: 'Provajder projekta',
    cableGroupOther: 'Drugi provajderi',
    cableDeclareTitle: 'Planiraj kabl',
    cableEditTitle: 'Izmeni planirani kabl',
    cableDialogHint: 'Koliko metara ovog kabla projektu treba.',
    cableSearch: 'Traži kabl ili provajdera…',
    cableDeclaredMetres: 'Planirani metri',
    cableNote: 'Napomena (neobavezno)',
    cableRemove: 'Ukloni iz plana',
    cableRemoveConfirm: 'Ukloniti ovaj kabl iz plana?',
    cableColOnSite: 'Na gradilištu (bubnjevi)',
    cableColUnaccounted: 'Neobjašnjeno',
    cableUnaccountedHint: 'Skinuto sa bubnjeva minus prijavljeno položeno od dolaska prvog bubnja — vidi Magacin → Usklađivanje.',
  },
})
